import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, updateMe, exportPersonalData } from '../utils/authApi';
import Notification from '../components/Notification';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import Info from '../components/elements/Info';
import Modal from '../components/Modal';
import ConsentSettings from '../components/ConsentSettings';
import QuickUnlockSetupPrompt from '../components/QuickUnlockSetupPrompt';
import { getQuickUnlockConfig, clearQuickUnlock } from '../utils/quickUnlock';
import { toDate } from '../utils/globalUtils';
import { User, Phone, UserCheck, Save, Download, AlertTriangle, Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';

export default function ProfilePage() {
    const navigate = useNavigate();
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const [role] = useState(sessionStorage.getItem('role'));
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [contact, setContact] = useState('');
    const [dob, setDob] = useState(null);
    const [address, setAddress] = useState('');
    const [referredBy, setReferredBy] = useState('');
    const [referredByUsername, setReferredByUsername] = useState('');
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [exportPasswordModalOpen, setExportPasswordModalOpen] = useState(false);
    const [exportPassword, setExportPassword] = useState('');
    const [exportPasswordError, setExportPasswordError] = useState(null);
    const [quickUnlockConfig, setQuickUnlockConfig] = useState(() => getQuickUnlockConfig());
    const [showQuickUnlockSetup, setShowQuickUnlockSetup] = useState(false);

    useEffect(() => {
        document.title = "Profile | Eternal Locker™";
        if (!isLoggedIn) return;
        setLoading(true);
        getMe().then((res) => {
            if (res.success && res.user) {
                setFirstName(res.user.firstName || '');
                setMiddleName(res.user.middleName || '');
                setLastName(res.user.lastName || '');
                setContact(res.user.contact || '');
                setDob(res.user.dob ? toDate(res.user.dob) : '');
                setAddress(res.user.address || '');
                setReferredBy(res.user.referredBy || '');
                setReferredByUsername(res.user.referredByUsername || '');
            } else {
                setNotification({ message: res.message || 'Failed to load profile.', type: 'error' });
            }
            setLoading(false);
        });
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!isLoggedIn) {
            setNotification({ message: 'Not authenticated.', type: 'error' });
            return;
        }

        const res = await updateMe({ firstName, middleName, lastName, contact, address });
        if (res.success) {
            setNotification({ message: 'Profile updated successfully!', type: 'success' });
            setTimeout(() => navigate('/dashboard'), 1000);
            sessionStorage.setItem('firstName', firstName);
            sessionStorage.setItem('lastName', lastName);
        } else {
            setNotification({ message: res.message || 'Failed to update profile.', type: 'error' });
        }
    };

    const handleQuickUnlockSetupDone = () => {
        setShowQuickUnlockSetup(false);
        setQuickUnlockConfig(getQuickUnlockConfig());
    };

    const handleTurnOffQuickUnlock = () => {
        clearQuickUnlock();
        setQuickUnlockConfig(null);
        setNotification({ message: 'Quick unlock turned off for this device.', type: 'success' });
    };

    const handleDownloadCsv = async () => {
        setExportPasswordModalOpen(true);
        setExportPassword('');
        setExportPasswordError(null);
    };

    const handleConfirmExport = async () => {
        if (!exportPassword) {
            setExportPasswordError('Password is required.');
            return;
        }

        setIsExporting(true);
        try {
            const result = await exportPersonalData(exportPassword, 'csv');
            if (!result?.success || !result.csv) {
                setNotification({ message: result?.message || 'Failed to export personal data.', type: 'error' });
                setExportPasswordError(result?.message || 'Export failed.');
                setIsExporting(false);
                return;
            }

            const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = result.filename || `personal-data-export-${Date.now()}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);

            setNotification({ message: 'Personal data CSV downloaded successfully.', type: 'success' });
        } catch {
            setNotification({ message: 'Failed to export personal data.', type: 'error' });
        } finally {
            setIsExporting(false);
            setExportPasswordModalOpen(false);
            setExportPassword('');
            setExportPasswordError(null);
        }
    };

    return (
        <Layout>
            <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />
            
            <div className="flex sm:flex-row flex-col justify-between items-center border-b border-accent-main pb-4">
                <h1 className="sm:text-3xl text-2xl font-extrabold text-accent-main flex items-center">
                    <User className="mr-3 w-7 h-7" />
                    {firstName || lastName ? `${firstName} ${lastName}`.trim() : 'Your Profile'}
                </h1>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3">
                <p className="text-lg text-primary-dark m-0">
                    Manage your personal information
                </p>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-accent-main mb-4"></div>
                    <p className="text-accent-main font-semibold text-lg">Loading your profile...</p>
                </div>
            ) : (
                <form className="space-y-8" onSubmit={handleSave}>
                    {/* Personal Information Section */}
                    <div className="bg-primary-light p-6 rounded-xl border border-accent-main">
                        <div className="flex items-center gap-2 mb-6">
                            <User className="w-6 h-6 text-accent-main" />
                            <h2 className="text-2xl font-bold text-primary-dark">Personal Information</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="relative">
                                <Input
                                    label="First Name"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    required
                                    placeholder="Enter your first name"
                                />
                            </div>
                            <div className="relative">
                                <Input
                                    label="Middle Name"
                                    value={middleName}
                                    onChange={e => setMiddleName(e.target.value)}
                                    placeholder="Enter your middle name"
                                />
                            </div>
                            <div className="relative">
                                <Input
                                    label="Last Name"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    required
                                    placeholder="Enter your last name"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="bg-primary-light p-6 rounded-xl border border-accent-main">
                        <div className="flex items-center gap-2 mb-6">
                            <Phone className="w-6 h-6 text-accent-main" />
                            <h2 className="text-2xl font-bold text-primary-dark">Contact Information</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="relative">
                                <Input
                                    label="Contact Number"
                                    value={contact}
                                    onChange={e => setContact(e.target.value)}
                                    required
                                    placeholder="Enter your contact number (e.g. +91-9876543210)"
                                />
                            </div>
                            <div className="relative">
                                <Input
                                    label="Date of Birth"
                                    type="date"
                                    value={dob}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="relative">
                                <Input
                                    label="Address"
                                    type="textarea"
                                    value={address}
                                    onChange={e => setAddress(e.target.value)}
                                    required
                                    placeholder="Enter your complete address"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Quick Unlock Section */}
                    {role !== 'nominee' && (
                        <div className="bg-primary-light p-6 rounded-xl border border-accent-main">
                            <div className="flex items-center gap-2 mb-4">
                                <ShieldCheck className="w-6 h-6 text-accent-main" />
                                <h2 className="text-2xl font-bold text-primary-dark">Quick Unlock</h2>
                            </div>
                            <p className="text-sm text-primary-dark mb-4">
                                Unlock the app on this device with a PIN or biometric instead of your password and email code every time.
                            </p>
                            {quickUnlockConfig?.enabled ? (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 text-primary-dark">
                                        {quickUnlockConfig.method === 'biometric' ? (
                                            <Fingerprint className="w-5 h-5 text-accent-main" />
                                        ) : (
                                            <KeyRound className="w-5 h-5 text-accent-main" />
                                        )}
                                        <span>
                                            {quickUnlockConfig.method === 'biometric' ? 'Biometric unlock is on' : 'PIN unlock is on'} for this device
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button type="secondary" onClick={() => setShowQuickUnlockSetup(true)}>
                                            Change
                                        </Button>
                                        <Button type="danger_outline" onClick={handleTurnOffQuickUnlock}>
                                            Turn Off
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button type="primary_outline" onClick={() => setShowQuickUnlockSetup(true)}>
                                    Set Up Quick Unlock
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Referral Information Section */}
                    {referredByUsername && (
                        <div className="bg-primary-light p-6 rounded-xl border border-accent-main">
                            <div className="flex items-center gap-2 mb-4">
                                <UserCheck className="w-6 h-6 text-accent-main" />
                                <h2 className="text-2xl font-bold text-primary-dark">Referral Information</h2>
                            </div>
                            
                            <Input
                                label="Referred By"
                                value={`${referredByUsername}`}
                                disabled
                            />
                            <p className="text-sm text-primary-dark mt-2">You were referred by this user</p>
                        </div>
                    )}

                    {/* Privacy Choices (Wave C, C1.7). Its own component because this
                        page is long enough already, and because the consent record has a
                        lifecycle of its own - it reloads itself after every change rather
                        than sharing this form's save button. A nominee has no consent
                        record to manage, and the server would 403 the read anyway. */}
                    {role !== 'nominee' && (
                        <ConsentSettings
                            onNotify={setNotification}
                            onRequestExport={() => setExportPasswordModalOpen(true)}
                        />
                    )}

                    {/* Action Buttons */}
                    {role !== 'nominee' && (
                        <div className="flex items-center justify-between gap-4 pt-6 border-t border-accent-main">
                            <Button
                                type="button"
                                onClick={handleDownloadCsv}
                                disabled={isExporting}
                                className="min-w-0 px-0 py-0 text-xs font-medium bg-transparent border-0 text-accent-main hover:bg-transparent"
                            >
                                <Download className="w-4 h-4" />
                                {isExporting ? 'Preparing CSV...' : 'My Data (CSV)'}
                            </Button>
                            <Button 
                                type="submit" 
                            >
                                <Save className="w-5 h-5" />
                                Save Changes
                            </Button>
                        </div>
                    )}
                </form>
            )}

            <Info className="mt-4">
                Keep your details up-to-date to ensure your nominees can access accurate information.
            </Info>

            {/* Export Personal Data Password Modal */}
            {exportPasswordModalOpen && (
                <Modal 
                    open={exportPasswordModalOpen} 
                    onClose={() => {
                        setExportPasswordModalOpen(false);
                        setExportPassword('');
                        setExportPasswordError(null);
                    }} 
                    title="Download Personal Data" 
                    icon={<Download />}
                >
                    <div className="space-y-4">
                        <div className="mb-4 text-accent-main bg-background-light p-3 rounded border border-accent-main flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm">To download your personal data, please confirm your identity by entering your account password. This is a security measure to protect your data.</p>
                        </div>
                        <Input
                            label="Password"
                            type="password"
                            value={exportPassword}
                            onChange={e => {
                                setExportPassword(e.target.value);
                                setExportPasswordError(null);
                            }}
                            error={exportPasswordError}
                            placeholder="Enter your password"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2 mt-6">
                            <Button
                                type="secondary"
                                onClick={() => {
                                    setExportPasswordModalOpen(false);
                                    setExportPassword('');
                                    setExportPasswordError(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleConfirmExport}
                                loading={isExporting}
                                disabled={isExporting}
                            >
                                Download
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            <QuickUnlockSetupPrompt
                open={showQuickUnlockSetup}
                accountEmail={sessionStorage.getItem('email')}
                onDone={handleQuickUnlockSetupDone}
            />
        </Layout>
    );
}

